import type { MenuOrder } from 'root/components/Menus/Settings/Tabs/MenusTab/MenusSettingsParams';

const COMPACT_PREFIX = '__C__';

const isNewFormat = (value: string): boolean => {
  return value.startsWith(COMPACT_PREFIX);
};

const removeCompactPrefix = (value: string): string => {
  return value.substring(COMPACT_PREFIX.length);
};

const uuidToBase64 = (uuid: string): string => {
  const hex = uuid.replace(/-/g, '');
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  const binary = String.fromCharCode(...bytes);
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const base64ToUuid = (base64: string): string => {
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const standardBase64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(standardBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const compressData = (menus: MenuOrder[]): string => {
  const encoded = menus.map((m) => `${uuidToBase64(m.id)}${m.checked ? 'T' : 'F'}`);
  const compact = encoded.join(':');
  return `${COMPACT_PREFIX}${compact}`;
};

const decompressData = (compactString: string): MenuOrder[] => {
  try {
    const encodedItems = compactString.split(':');

    return encodedItems.map((item) => {
      const checked = item.slice(-1) === 'T';
      const base64Uuid = item.slice(0, -1);
      const uuid = base64ToUuid(base64Uuid);
      return {
        id: uuid,
        checked,
      };
    });
  } catch {
    return [];
  }
};

const parseOldFormat = (jsonString: string): MenuOrder[] => {
  return JSON.parse(jsonString);
};

const parseNewFormat = (value: string): MenuOrder[] => {
  const compactString = removeCompactPrefix(value);
  return decompressData(compactString);
};

export const deserializeMenusDisplayOrder = (value: string | undefined): MenuOrder[] | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    const parsed = isNewFormat(value) ? parseNewFormat(value) : parseOldFormat(value);
    return parsed.length > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const serializeMenusDisplayOrder = (menus: MenuOrder[], useChunking: boolean): string => {
  if (!useChunking || menus.length === 0) {
    return JSON.stringify(menus);
  }
  return compressData(menus);
};
